import { useState, useEffect, useRef, useCallback } from 'react';
import qs from 'qs';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Title,
  Grid,
  Pagination,
  Card,
  Table,
  Badge,
  Button,
  Group,
  Box,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileExcel, IconBuildingCommunity } from '@tabler/icons-react';

import * as actions from '../../../actions/Lote';
import SmartMapLegend from '../../../components/Map/SmartMapLegend';
import SmartMap, { mapKey } from '../../../components/Map/SmartMapFallback';
import Find from './Find';
import ListLoadingOverlay from '../../../components/ListLoadingOverlay';
import EmptyState from '../../../components/EmptyState';
import { DetailSkeleton } from '../../../components/TableSkeleton';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { qsNumber, qsString } from '../../../utils/qsHelpers';
import { useOptionalNavegadorMap } from '../../../components/Navegador/NavegadorMapContext';
import type { ImovelLoteItem, ResultsList } from '../../../store/types';

import './list.less';

const DEFAULTS = { filterText: '', filterExtent: '', pageSize: 6, page: 1 };

interface ListFilters {
  page?: number;
  SEARCH?: string;
  EXTENT?: unknown;
}

export default function ImovelList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const results = useAppSelector((state) => state.lote.results);
  const loading = useAppSelector((state) => state.container.loading);
  const publicSettings = useAppSelector((state) => state.publicSettings.settings);

  const smartMapApiRef = useRef<unknown>(null);
  // Quando renderizado dentro do NavegadorShell o shell dono do mapa + search.
  // Suprimimos nossos próprios <SmartMap> + <Find> + <Title> pra evitar duplicação.
  const navegadorMap = useOptionalNavegadorMap();
  const inShell = !!navegadorMap;
  // Extract stable setter so the effect below doesn't loop on context value changes.
  const setShellGeoJson = navegadorMap?.setGeoJson;

  // Publica geoJson selecionada (futuro: highlight de lote selecionado) e mantém
  // overlay alinhado com results carregados.
  useEffect(() => {
    if (!setShellGeoJson) return;
    if (results?.items) {
      setShellGeoJson(results.items.map((x: any) => x.geoJson).filter(Boolean));
    } else {
      setShellGeoJson(null);
    }
  }, [setShellGeoJson, results]);

  const [page, setPage] = useState<number>(DEFAULTS.page);
  const [pageSize] = useState<number>(DEFAULTS.pageSize);
  const [filterText, setFilterText] = useState('');
  const [filterExtent, setFilterExtent] = useState('');
  const [filterMessage, setFilterMessage] = useState<string | null>('');
  const [loadingExporXlsx, setLoadingExporXlsx] = useState(false);
  const [menorRnum, setMenorRnum] = useState<number>(1);
  const [maoirRnum, setMaoirRnum] = useState<number>(DEFAULTS.pageSize);

  const executeSearch = useCallback(() => {
    const query = location.search
      ? qs.parse(location.search, { ignoreQueryPrefix: true })
      : null;

    if (query && !query.z) {
      const fText = qsString(query.q, DEFAULTS.filterText);
      const fExtent = qsString(query.e, DEFAULTS.filterExtent);
      let fMessage: string | null = null;
      if (fText && fText.length > 0) fMessage = `Filtrado por "${fText}"`;
      else if (fExtent && fExtent.length > 0) fMessage = 'Filtrado espacialmente';

      const pg = qsNumber(query.page, DEFAULTS.page);
      const menor = ((pg - 1) * DEFAULTS.pageSize) + 1;
      const maior = pg * DEFAULTS.pageSize;

      setPage(pg);
      setFilterText(fText);
      setFilterExtent(fExtent);
      setFilterMessage(fMessage);
      setMenorRnum(menor);
      setMaoirRnum(maior);

      const filters: ListFilters = {};
      if (fText && fText.trim().length > 0) filters.SEARCH = fText;
      else if (fExtent) filters.EXTENT = JSON.parse(`[${fExtent}]`);

      dispatch((actions as any).list(
        { page: pg, pageSize: DEFAULTS.pageSize, filterText: fText, filterExtent: fExtent, filters },
        ({ data }: { data: ResultsList<ImovelLoteItem> | null }) => {
          if (data && data.totalDistinct === 1) {
            const item = data.items[0];
            if (item.loteId) navigate(`/navegador-geofinanceiro/lote/${item.loteId}`, { state: { filterText: fText } });
            else if (item.id) navigate(`/navegador-geofinanceiro/imovel/${item.id}`);
          }
        },
      ));
    } else {
      setFilterText('');
      setFilterExtent('');
      setFilterMessage('');
      dispatch((actions as any).clearList());
    }
  }, [location.search, dispatch, navigate]);

  useEffect(() => { executeSearch(); }, [executeSearch]);

  useEffect(() => {
    return () => { dispatch((actions as any).clearList()); };
  }, [dispatch]);

  const identifyOnExtent = (geographicExtent: number[]) => {
    if (!publicSettings?.mapaInterativoActive) return;

    dispatch((actions as any).identifyOnExtent(geographicExtent, ({ data }: { data: number[] | null }) => {
      if (!data || data.length === 0) {
        notifications.show({ message: 'Nenhum lote encontrado.', color: 'yellow' });
      } else if (data.length === 1) {
        navigate(`/navegador-geofinanceiro/lote/${data[0]}`);
      } else {
        navigate(`/navegador-geofinanceiro?e=${JSON.stringify(geographicExtent).slice(1, -1)}`);
      }
    }));
  };

  const pageChange = (newPage: number) => {
    const query: Record<string, unknown> = { page: newPage };
    if (filterText) query.q = filterText;
    else if (filterExtent) query.e = filterExtent;
    navigate(`/navegador-geofinanceiro?${qs.stringify(query)}`);
  };

  const clickExportXlsx = () => {
    const filters: ListFilters = { page };
    if (filterText) filters.SEARCH = filterText;
    else if (filterExtent) filters.EXTENT = JSON.parse(`[${filterExtent}]`);
    setLoadingExporXlsx(true);
    dispatch((actions as any).exportXlsx({ page, pageSize, filters }));
    setTimeout(() => setLoadingExporXlsx(false), 10000);
  };

  const renderItemResult = (item: ImovelLoteItem) => {
    let className = 'card-imovel ';
    if (item.status) className += item.status;
    if (!item.loteId) className += ' sem-lote';

    return (
      <Grid.Col key={item.id} span={{ base: 12, md: 6 }}>
        <Card withBorder shadow="sm" padding="sm" className={className}>
          <Card.Section withBorder inheritPadding py="xs">
            {!item.loteId ? (
              <Group justify="space-between">
                <Link to={`/navegador-geofinanceiro/imovel/${item.id}`}>{item.inscricao}</Link>
                <Badge color="red" variant="light">Sem Lote</Badge>
              </Group>
            ) : (
              <Link
                to={`/navegador-geofinanceiro/lote/${item.loteId}`}
                state={{ filterText }}
              >
                {item.inscricao}
              </Link>
            )}
          </Card.Section>
          <Table className="extra-small" mt="xs">
            <Table.Tbody>
              {item.fieldItemGroups[0].fieldItems.map((fi) => (
                <Table.Tr key={fi.fieldDefinitionId}>
                  <Table.Td style={{ width: 200 }}>
                    <Text component="span" fw={600}>{fi.label}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1} title={fi.value}>{fi.value}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      </Grid.Col>
    );
  };

  const totalPages = results ? Math.max(1, Math.ceil(results.total / pageSize)) : 1;

  return (
    <Box>
      {!inShell && (
        <>
          <Title order={1}>
            {results
              ? `Listagem de Inscrições do ${publicSettings?.navegadorGeofinanceiroAbaName}`
              : publicSettings?.navegadorGeofinanceiroAbaName}
          </Title>

          <Find />
        </>
      )}

      <Grid mt="md">
        <Grid.Col span={{ base: 12, md: inShell ? 12 : 7 }} offset={{ base: 0, md: inShell ? 0 : 2 }}>
          <Text fw={600}>{filterMessage}</Text>
        </Grid.Col>
      </Grid>

      {loading && !results && (
        <Grid className="list-card-item" gap="md" mt="md">
          {Array.from({ length: 6 }, (_, index) => (
            <Grid.Col key={index} span={{ base: 12, md: 6 }}>
              <Card withBorder shadow="sm" padding="sm"><DetailSkeleton lines={5} /></Card>
            </Grid.Col>
          ))}
        </Grid>
      )}

      {results && (
        <Grid align="center" mt="xs">
          <Grid.Col span={{ base: 12, md: 7 }} offset={{ base: 0, md: 2 }}>
            <Group justify="space-between">
              {publicSettings?.listagemExportaExcelActive && (
                <Button
                  variant="default"
                  leftSection={<IconFileExcel size={16} />}
                  disabled={results.total === 0}
                  onClick={clickExportXlsx}
                  loading={loadingExporXlsx}
                >
                  Exportar Xlsx
                </Button>
              )}
              <Text size="sm" c="dimmed">
                Listando {menorRnum} - {maoirRnum < results.total ? maoirRnum : results.total} de {results.total} registros.
              </Text>
            </Group>
          </Grid.Col>
        </Grid>
      )}

      {!results && !inShell && (
        <Grid mt="md" style={{ minHeight: 'calc(100vh - 315px)' }}>
          <Grid.Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Box style={{ position: 'relative', flex: 1, minHeight: 400 }}>
              <SmartMap
                mapKey={mapKey.SMARTPUBLIC}
                onApiReady={(api: unknown) => { smartMapApiRef.current = api; }}
                identifyOnExtent={identifyOnExtent}
                controls={null}
              />
            </Box>
            <Group mt="xs">
              <Text fw={600}>Legenda</Text>
              <SmartMapLegend />
            </Group>
          </Grid.Col>
        </Grid>
      )}

      {results && (
        <Box mt="md">
          <ListLoadingOverlay loading={loading}>
            {results.items.length === 0 ? (
              <EmptyState
                label="Nenhuma inscrição encontrada"
                submessage="Tente outro termo de busca ou área no mapa."
                icon={IconBuildingCommunity}
                mih={240}
              />
            ) : (
              <Grid className="list-card-item" gap="md">
                {results.items.map(renderItemResult)}
              </Grid>
            )}
          </ListLoadingOverlay>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination total={totalPages} value={page} onChange={pageChange} />
            </Group>
          )}
        </Box>
      )}
    </Box>
  );
}
